import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Alert, Box, Button, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import Spinner from '~/components/Common/Spinner';
import { BundleItemOption } from '~/components/Cruises/pages/Bundles/types';

import { useDeleteBundleItemOption } from '~/hooks/apis/cruises/useBundleItemOptionMutations';
import { useGetBundleItemOptions } from '~/hooks/apis/cruises/useGetBundleItemOptions';

import { formatDateShort } from '~/services/TimeService';

import BundleItemOptionCreate from './BundleItemOptionCreate';

type Props = {
  bundleItemId: string;
};

function BundleItemOptionList({ bundleItemId }: Props) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteButton, setDeleteButton] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { bundleId } = useParams<{ bundleId: string }>();

  const { data: options = [], isLoading, error } = useGetBundleItemOptions(bundleId, bundleItemId);
  const deleteMutation = useDeleteBundleItemOption();

  const handleDelete = useCallback(
    async (optionId: string) => {
      try {
        await deleteMutation.mutateAsync({ bundleId, itemId: bundleItemId, optionId });
        enqueueSnackbar('Option deleted', { variant: 'success' });
        setDeleteButton(null);
      } catch (err) {
        enqueueSnackbar('Error deleting option', { variant: 'error' });
      }
    },
    [bundleId, bundleItemId, enqueueSnackbar, deleteMutation],
  );

  const handleOpenCreate = useCallback(() => {
    setShowCreateDialog(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setShowCreateDialog(false);
  }, []);

  const actionColumn = useCallback(
    (_cell: unknown, row: BundleItemOption) => (
      <Box>
        {deleteButton !== row.id && (
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDeleteButton(row.id);
            }}
          >
            Delete
          </Button>
        )}

        {deleteButton === row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await handleDelete(row.id);
              }}
            >
              Confirm
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    ),
    [deleteButton, handleDelete],
  );

  const columns: Array<GridColDef> = [
    {
      field: 'leOfferId',
      headerName: 'LE Offer ID',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      display: 'flex',
    },
    {
      field: 'checkIn',
      headerName: 'Check-in',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => formatDateShort(params.value),
      display: 'flex',
    },
    {
      field: 'checkOut',
      headerName: 'Check-out',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => formatDateShort(params.value),
      display: 'flex',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 180,
      sortable: false,
      renderCell: (params) => actionColumn(params.value, params.row),
      display: 'flex',
    },
  ];

  if (isLoading) {
    return <Spinner size={20} />;
  }

  if (error) {
    return <Alert severity="error">Error loading options: {error.message}</Alert>;
  }

  return (
    <Box>
      <DataGrid rows={options} columns={columns} getRowId={(row: BundleItemOption) => row.id} autoHeight hideFooter />

      <Stack mt={1} width="100%" direction="row" justifyContent="flex-end">
        <Button variant="outlined" size="small" onClick={handleOpenCreate} fullWidth>
          Add Option
        </Button>
      </Stack>

      {showCreateDialog && <BundleItemOptionCreate bundleItemId={bundleItemId} onClose={handleCloseCreate} />}
    </Box>
  );
}

export default BundleItemOptionList;
